/**
 * YouTube Studio — sección completa con tabs:
 *  - Dashboard (canal métricas)
 *  - Title Research (búsqueda competidores + scoring)
 *  - Ideas (cola de vídeos a producir)
 *  - Clipping Studio (cola de cortes multi-plataforma)
 *  - Cuentas Sociales (multi-cuenta TikTok/Instagram/X)
 *  - Calendario (sincronizado con tareas + bookings)
 *  - Agentes YouTube (los 30 agentes del sistema en una vista)
 */
import { h, apiClient, formatEur, btn, toast, emptyState, skeletonCard, openModal } from './_common.js';

let _ytTab = 'dashboard';

export async function renderYouTube(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'max-w-7xl mx-auto px-6 py-6 space-y-6' });
  root.appendChild(wrap);

  // Header
  wrap.appendChild(h('div', { class: 'flex items-center justify-between' }, [
    h('div', {}, [
      h('h2', { class: 'font-display text-2xl font-bold' }, '🎬 YouTube Studio'),
      h('p', { class: 'text-sm text-ink-300' }, 'Métricas, research, clipping, multi-cuenta, calendario unificado.'),
    ]),
  ]));

  // Quick links — cuentas YouTube/TikTok/IG/X
  try {
    const { renderQuickLinks } = await import('./_quick-links.js');
    const qlContainer = h('div');
    wrap.appendChild(qlContainer);
    await renderQuickLinks(qlContainer, 'youtube', {
      title: '🔗 Mis cuentas de creator',
      subtitle: 'YouTube Studio, TikTok, Instagram, X — acceso directo a cada cuenta.',
      onReload: () => renderYouTube(root),
    });
  } catch { /* skip */ }

  // Tabs
  const tabs = [
    { id: 'studio',     label: '🚀 Studio PRO' },
    { id: 'dashboard',  label: '📊 Canal' },
    { id: 'research',   label: '🔍 Title Research' },
    { id: 'ideas',      label: '💡 Ideas' },
    { id: 'clipia',     label: '🎬 Clip IA' },
    { id: 'clipping',   label: '✂️ Clipping manual' },
    { id: 'cuentas',    label: '📱 Cuentas' },
    { id: 'calendario', label: '📅 Calendario' },
    { id: 'analytics',  label: '📊 Analytics' },
    { id: 'agentes',    label: '🤖 Agentes YT' },
  ];
  const tabBar = h('div', { class: 'flex gap-1 border-b border-base-700 overflow-x-auto' });
  tabs.forEach((t) => {
    const isActive = t.id === _ytTab;
    tabBar.appendChild(h('button', {
      class: `px-3 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${isActive ? 'border-gold-400 text-gold-300' : 'border-transparent text-ink-300 hover:text-ink-100'}`,
      onclick: () => { _ytTab = t.id; renderYouTube(root); },
    }, t.label));
  });
  wrap.appendChild(tabBar);

  const content = h('div', {});
  wrap.appendChild(content);

  if (_ytTab === 'studio')     await renderStudioPro(content, root);
  if (_ytTab === 'dashboard')  await renderDashboard(content, root);
  if (_ytTab === 'research')   await renderResearch(content, root);
  if (_ytTab === 'ideas')      await renderIdeas(content, root);
  if (_ytTab === 'clipia')     await renderClipIA(content, root);
  if (_ytTab === 'clipping')   await renderClipping(content, root);
  if (_ytTab === 'cuentas')    await renderCuentasYT(content, root);
  if (_ytTab === 'calendario') await renderCalendario(content, root);
  if (_ytTab === 'analytics')  await renderAnalyticsClipIA(content, root);
  if (_ytTab === 'agentes')    await renderAgentes(content);
}

// ============= DASHBOARD =============
async function renderDashboard(content, root) {
  content.appendChild(skeletonCard(2));
  const r = await apiClient.get('/api/v1/youtube/channel').catch(() => ({ channel: null }));
  content.innerHTML = '';

  if (!r.channel) {
    content.appendChild(setupChannelForm(root));
    return;
  }
  const c = r.channel;
  content.appendChild(h('div', { class: 'grid grid-cols-1 md:grid-cols-4 gap-3 mb-6' }, [
    kpi('Suscriptores', (c.subscribers || 0).toLocaleString(), 'text-gold-300', c.channelHandle || c.channelName || '—'),
    kpi('Visualizaciones', (c.totalViews || 0).toLocaleString(), 'text-emerald-400', 'totales'),
    kpi('Vídeos', String(c.totalVideos || 0), 'text-ink-100', 'publicados'),
    kpi('Última sync', c.lastSyncAt ? new Date(c.lastSyncAt).toLocaleString() : 'nunca', 'text-ink-300', c.apiKey ? '✓ API configurada' : '⚠ sin API key'),
  ]));

  content.appendChild(h('div', { class: 'flex gap-2 mb-4' }, [
    btn('🔄 Sync ahora', { class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-4 py-2 rounded-lg text-sm', onclick: async () => {
      const t = toast('Sincronizando canal...');
      try {
        await apiClient.post('/api/v1/youtube/channel/sync', {});
        t.update('Canal sincronizado', 'success');
        renderYouTube(root);
      } catch (e) { t.update('Sync fallido: ' + (e?.message || e), 'error'); }
    } }),
    btn('⚙️ Reconfigurar', { class: 'bg-base-700 hover:bg-base-600 text-ink-100 px-4 py-2 rounded-lg text-sm', onclick: () => {
      content.innerHTML = '';
      content.appendChild(setupChannelForm(root));
    } }),
  ]));

  // Recent videos
  const recentRes = await apiClient.get('/api/v1/youtube/channel/recent').catch(() => ({ videos: [] }));
  if (recentRes.videos && recentRes.videos.length > 0) {
    content.appendChild(h('h3', { class: 'font-semibold mb-2' }, '📺 Últimos vídeos publicados'));
    content.appendChild(h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' },
      recentRes.videos.map((v) => h('a', { href: v.url, target: '_blank', rel: 'noopener', class: 'bg-base-800/50 border border-base-700 rounded-lg overflow-hidden hover:border-gold-400/40 transition block' }, [
        v.thumbnail ? h('img', { src: v.thumbnail, class: 'w-full', loading: 'lazy' }) : null,
        h('div', { class: 'p-3' }, [
          h('div', { class: 'font-semibold text-sm line-clamp-2 mb-1' }, v.title),
          h('div', { class: 'text-xs text-ink-400' }, `${(v.viewCount || 0).toLocaleString()} views · ${(v.daysSincePublish || 0)}d · ${(v.velocityViewsPerDay || 0).toFixed(0)}/día`),
        ]),
      ])),
    ));
  } else if (c.apiKey) {
    content.appendChild(emptyState('📺', 'Sin vídeos recientes', 'El canal no tiene vídeos publicados todavía o la API no devolvió resultados.'));
  }
}

function setupChannelForm(root) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-6' }, [
    h('h3', { class: 'font-display text-lg font-semibold mb-2' }, '⚙️ Configura tu canal YouTube'),
    h('p', { class: 'text-sm text-ink-300 mb-4' }, [
      'Necesitas una ',
      h('a', { href: 'https://console.cloud.google.com/apis/credentials', target: '_blank', class: 'text-gold-300 hover:underline' }, 'YouTube Data API v3 key'),
      ' (gratis, 10.000 quota/día). Crea un proyecto Google Cloud → APIs → habilita YouTube Data v3 → Credenciales → API key.',
    ]),
    (() => {
      const handleInput = h('input', { type: 'text', placeholder: '@TuCanal o ID', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm mb-2' });
      const keyInput = h('input', { type: 'password', placeholder: 'AIzaSy...', class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm mb-3' });
      return h('div', {}, [
        h('label', { class: 'text-xs text-ink-400 mb-1 block' }, 'Handle o ID canal:'),
        handleInput,
        h('label', { class: 'text-xs text-ink-400 mb-1 block' }, 'API key:'),
        keyInput,
        btn('💾 Guardar y sincronizar', { class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-4 py-2 rounded-lg text-sm', onclick: async () => {
          const handle = handleInput.value.trim();
          const key = keyInput.value.trim();
          if (!key) return toast('API key requerida', 'error');
          const t = toast('Guardando + sync...');
          try {
            const r = await apiClient.post('/api/v1/youtube/channel/setup', { channelHandle: handle, apiKey: key });
            t.update(r.statsFetched ? 'Canal configurado y sincronizado' : 'Guardado (pero sync no fue posible)', 'success');
            renderYouTube(root);
          } catch (e) { t.update('Error: ' + (e?.message || e), 'error'); }
        } }),
      ]);
    })(),
  ]);
}

// ============= TITLE RESEARCH =============
async function renderResearch(content, root) {
  const queryInput = h('input', { type: 'text', placeholder: 'Ej: "como invertir en oro 2026"', class: 'flex-1 bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' });
  const results = h('div', { class: 'mt-4' });

  content.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4 mb-4' }, [
    h('h3', { class: 'font-semibold mb-2' }, '🔍 Title Research'),
    h('p', { class: 'text-xs text-ink-300 mb-3' }, 'Dime de qué tipo de vídeo quieres hacer y busco 25 vídeos similares, comparo títulos, miradas/sub, velocidad y tamaño canal para identificar EL TÍTULO ganador.'),
    h('div', { class: 'flex gap-2' }, [
      queryInput,
      btn('🔍 Investigar', { class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-4 py-2 rounded-lg text-sm whitespace-nowrap', onclick: async () => {
        const q = queryInput.value.trim();
        if (!q) return;
        results.innerHTML = '';
        results.appendChild(skeletonCard(4));
        try {
          const r = await apiClient.post('/api/v1/youtube/title-research', { query: q, maxResults: 25 });
          renderResearchResults(results, r, root);
        } catch (e) {
          results.innerHTML = '';
          results.appendChild(h('div', { class: 'text-red-400 text-sm' }, 'Error: ' + (e?.message || e)));
        }
      } }),
    ]),
  ]));
  content.appendChild(results);
}

function renderResearchResults(container, r, root) {
  container.innerHTML = '';
  if (r.warning) {
    container.appendChild(h('div', { class: 'bg-amber-500/10 border border-amber-400/30 rounded-lg p-4 mb-3 text-sm text-amber-300' }, r.warning));
    return;
  }
  if (!r.competitors || r.competitors.length === 0) {
    container.appendChild(emptyState('🔍', 'Sin resultados', 'La búsqueda no devolvió vídeos. Prueba otra query.'));
    return;
  }

  // TOP 5 títulos ganadores
  container.appendChild(h('h3', { class: 'font-display font-semibold text-base mb-3' }, '🏆 Top 5 títulos por viralidad'));
  container.appendChild(h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6' },
    r.topTitles.map((t, i) => h('div', { class: `border ${i === 0 ? 'border-gold-400 bg-gold-400/5' : 'border-base-700 bg-base-800/50'} rounded-lg overflow-hidden` }, [
      t.thumbnail ? h('img', { src: t.thumbnail, class: 'w-full', loading: 'lazy' }) : null,
      h('div', { class: 'p-3 space-y-1' }, [
        h('div', { class: 'flex items-center gap-2 mb-1' }, [
          h('span', { class: `text-lg font-bold ${i === 0 ? 'text-gold-300' : 'text-ink-200'}` }, `#${i + 1}`),
          h('span', { class: 'text-xs text-ink-400 ml-auto' }, `score ${t.score.toFixed(1)}`),
        ]),
        h('div', { class: 'font-semibold text-sm line-clamp-2' }, t.title),
        h('div', { class: 'text-xs text-ink-400' }, `${t.channel} · ${(t.subs || 0).toLocaleString()} subs`),
        h('div', { class: 'text-xs space-x-3 text-ink-300 font-mono' }, [
          h('span', {}, `👁 ${(t.views || 0).toLocaleString()}`),
          h('span', {}, `📈 ${t.viewsPerSub.toFixed(2)}x`),
          h('span', {}, `⚡ ${(t.velocity || 0).toFixed(0)}/d`),
          h('span', {}, `📅 ${t.days || 0}d`),
        ]),
        h('a', { href: t.url, target: '_blank', class: 'text-xs text-gold-300 hover:underline mt-2 block' }, 'Ver en YouTube →'),
      ]),
    ])),
  ));

  // Botón crear idea con el mejor título
  if (r.topTitles[0]) {
    container.appendChild(btn(`✨ Usar "${r.topTitles[0].title.slice(0, 40)}..." como idea`, { class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-4 py-2 rounded-lg text-sm mb-6', onclick: async () => {
      await apiClient.post('/api/v1/youtube/ideas', { topic: r.topTitles[0].title, workingTitle: r.topTitles[0].title });
      toast('Idea guardada en "Ideas"', 'success');
      _ytTab = 'ideas';
      renderYouTube(root);
    } }));
  }

  // Lista completa competidores
  container.appendChild(h('h3', { class: 'font-display font-semibold text-base mb-3' }, `📊 Top 25 competidores (ordenados por score)`));
  container.appendChild(h('div', { class: 'space-y-1' },
    r.competitors.slice(0, 25).map((v) => h('a', { href: v.url, target: '_blank', class: 'flex items-center gap-2 bg-base-800/30 border border-base-700 rounded p-2 text-xs hover:border-gold-400/40' }, [
      v.thumbnail ? h('img', { src: v.thumbnail, class: 'w-16 h-9 object-cover rounded' }) : null,
      h('div', { class: 'flex-1' }, [
        h('div', { class: 'font-medium text-sm line-clamp-1' }, v.title),
        h('div', { class: 'text-ink-400' }, `${v.channelTitle} · ${(v.viewCount || 0).toLocaleString()} views · ${(v.viewsPerSub || 0).toFixed(2)}/sub`),
      ]),
      h('span', { class: 'text-gold-300 font-mono' }, v.score.toFixed(1)),
    ])),
  ));
}

// ============= IDEAS =============
async function renderIdeas(content, root) {
  const r = await apiClient.get('/api/v1/youtube/ideas').catch(() => ({ ideas: [] }));
  content.innerHTML = '';

  content.appendChild(h('div', { class: 'flex items-center justify-between mb-4' }, [
    h('h3', { class: 'font-semibold' }, `💡 Ideas de vídeos (${r.ideas.length})`),
    btn('+ Nueva idea', { class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-3 py-1.5 rounded-lg text-sm', onclick: async () => {
      const data = await openModal({ title: 'Nueva idea de vídeo', fields: [
        { name: 'topic', label: 'Tema/Topic', required: true },
        { name: 'workingTitle', label: 'Título de trabajo (opcional)' },
        { name: 'scriptOutline', label: 'Outline script (opcional)', type: 'textarea' },
      ] });
      if (!data) return;
      await apiClient.post('/api/v1/youtube/ideas', data);
      toast('Idea creada', 'success');
      renderYouTube(root);
    } }),
  ]));

  if (r.ideas.length === 0) {
    content.appendChild(emptyState('💡', 'Sin ideas todavía', 'Empieza por la pestaña "Title Research" para detectar qué títulos están explotando ahora.'));
    return;
  }

  content.appendChild(h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-3' },
    r.ideas.map((i) => h('div', { class: 'bg-base-800/50 border border-base-700 rounded-lg p-4' }, [
      h('div', { class: 'flex items-start justify-between mb-2' }, [
        h('div', {}, [
          h('div', { class: 'font-semibold text-sm' }, i.workingTitle || i.topic),
          h('div', { class: 'text-xs text-ink-400' }, i.topic),
        ]),
        h('span', { class: `text-xs px-2 py-0.5 rounded-full border ${i.status === 'published' ? 'border-emerald-400/40 text-emerald-300' : 'border-base-600 text-ink-300'}` }, i.status),
      ]),
      i.scriptOutline ? h('p', { class: 'text-xs text-ink-300 line-clamp-3' }, i.scriptOutline) : null,
    ])),
  ));
}

// ============= CLIPPING STUDIO =============
async function renderClipping(content, root) {
  const r = await apiClient.get('/api/v1/youtube/clips').catch(() => ({ clips: [] }));
  content.innerHTML = '';

  content.appendChild(h('div', { class: 'bg-emerald-900/10 border border-emerald-400/30 rounded-xl p-4 mb-4' }, [
    h('h3', { class: 'font-semibold text-sm mb-2 text-emerald-300' }, '✂️ Clipping Studio multi-cuenta'),
    h('p', { class: 'text-xs text-ink-300 mb-3' }, [
      'Flujo asistido: subes vídeo largo → la IA detecta los mejores momentos (hook+retention) → genera 3-5 clips × 15 captions distintos × hashtags optimizados → cola visible aquí. ',
      h('strong', {}, 'Tú abres cada cuenta TikTok/Instagram en tu móvil y subes en 8 minutos total.'),
      ' 0% riesgo ban (subes manual desde tu device).',
    ]),
    h('div', { class: 'flex gap-2 flex-wrap' }, [
      btn('+ Nuevo clip manual', { class: 'bg-emerald-600/80 hover:bg-emerald-600 text-white px-3 py-1.5 rounded text-sm', onclick: async () => {
        const data = await openModal({ title: 'Crear clip', fields: [
          { name: 'sourceVideoTitle', label: 'Vídeo origen (título)', required: true },
          { name: 'sourceVideoUrl', label: 'URL vídeo origen' },
          { name: 'clipTitle', label: 'Título del clip (gancho)', required: true },
          { name: 'startSec', label: 'Inicio (segundos)', type: 'number', required: true },
          { name: 'endSec', label: 'Fin (segundos)', type: 'number', required: true },
          { name: 'caption', label: 'Caption principal', type: 'textarea' },
        ] });
        if (!data) return;
        try {
          await apiClient.post('/api/v1/youtube/clips', {
            ...data,
            startSec: parseInt(data.startSec, 10),
            endSec: parseInt(data.endSec, 10),
            hookScore: 0.7,
          });
          toast('Clip añadido a cola', 'success');
          renderYouTube(root);
        } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
      } }),
      btn('🧠 Generar variantes IA', { class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-3 py-1.5 rounded text-sm', onclick: async () => {
        const niche = (await openModal({ title: 'Generar variantes virales IA', fields: [
          { name: 'niche', label: 'Nicho', type: 'select', required: true, options: [
            { value: 'autosell', label: '🚗 Autosell' },
            { value: 'inmobiliaria', label: '🏠 Inmobiliaria' },
            { value: 'trading', label: '📈 Trading' },
            { value: 'emprender', label: '💼 Emprender' },
            { value: 'lifestyle', label: '✨ Lifestyle' },
            { value: 'ai_agency', label: '🤖 AI Agency' },
          ] },
          { name: 'count', label: 'Variantes a generar', type: 'number', value: 3 },
        ] }));
        if (!niche) return;
        try {
          const r = await apiClient.post('/api/v1/youtube/studio/generate', { niche: niche.niche, language: 'es', count: Number(niche.count) || 3 });
          toast(`✅ ${(r.variants || []).length} variantes generadas · ve a Studio PRO`, 'success');
          _ytTab = 'studio';
          renderYouTube(root);
        } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
      } }),
    ]),
  ]));

  if (r.clips.length === 0) {
    content.appendChild(emptyState('✂️', 'Sin clips en cola', 'Crea un clip manual o sube un vídeo largo para que la IA detecte los mejores momentos.'));
    return;
  }

  // Filtrar por status
  const queued = r.clips.filter((c) => c.status === 'queued');
  const uploaded = r.clips.filter((c) => c.status === 'uploaded');

  if (queued.length > 0) {
    content.appendChild(h('h4', { class: 'font-semibold mt-4 mb-2' }, `🟡 En cola (${queued.length})`));
    content.appendChild(h('div', { class: 'space-y-2' }, queued.map((c) => clipCard(c, root))));
  }
  if (uploaded.length > 0) {
    content.appendChild(h('h4', { class: 'font-semibold mt-6 mb-2' }, `✅ Subidos (${uploaded.length})`));
    content.appendChild(h('div', { class: 'space-y-2' }, uploaded.slice(0, 20).map((c) => clipCard(c, root))));
  }
}

function clipCard(c, root) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-lg p-3' }, [
    h('div', { class: 'flex items-start justify-between mb-2' }, [
      h('div', { class: 'flex-1' }, [
        h('div', { class: 'font-semibold text-sm' }, c.clipTitle),
        h('div', { class: 'text-xs text-ink-400' }, `de "${c.sourceVideoTitle}" · ${c.duration}s · score ${(c.hookScore || 0).toFixed(2)}`),
      ]),
      c.status === 'queued' && btn('✓ Marcar subido', { class: 'bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs px-2 py-1 rounded', onclick: async () => {
        await apiClient.post(`/api/v1/youtube/clips/${c.id}/uploaded`, {});
        toast('Marcado como subido', 'success');
        renderYouTube(root);
      } }),
    ]),
    c.caption ? h('p', { class: 'text-xs text-ink-200 bg-base-900/50 rounded p-2 mb-2' }, c.caption) : null,
    Array.isArray(c.targetPlatforms) && c.targetPlatforms.length > 0 ? h('div', { class: 'flex gap-1 text-xs' },
      c.targetPlatforms.map((p) => h('span', { class: 'bg-base-700 text-ink-200 px-2 py-0.5 rounded' }, p))) : null,
  ]);
}

// ============= CUENTAS SOCIALES =============
async function renderCuentas(content, root) {
  const r = await apiClient.get('/api/v1/youtube/social-accounts').catch(() => ({ accounts: [] }));
  content.innerHTML = '';

  content.appendChild(h('div', { class: 'flex items-center justify-between mb-4' }, [
    h('h3', { class: 'font-semibold' }, `📱 Cuentas sociales (${r.accounts.length})`),
    btn('+ Añadir cuenta', { class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-3 py-1.5 rounded-lg text-sm', onclick: async () => {
      const data = await openModal({ title: 'Nueva cuenta social', fields: [
        { name: 'platform', label: 'Plataforma', type: 'select', options: [
          { value: 'tiktok', label: 'TikTok' },
          { value: 'instagram', label: 'Instagram' },
          { value: 'youtube-shorts', label: 'YouTube Shorts' },
          { value: 'x', label: 'X (Twitter)' },
        ], required: true },
        { name: 'accountHandle', label: 'Handle (sin @)', required: true },
        { name: 'niche', label: 'Nicho (ej: trading, inmuebles)' },
        { name: 'uploadStrategy', label: 'Estrategia upload', type: 'select', options: [
          { value: 'manual', label: 'Manual (yo subo)' },
          { value: 'postiz', label: 'Postiz/Blotato' },
          { value: 'api', label: 'API oficial' },
        ] },
      ] });
      if (!data) return;
      try {
        await apiClient.post('/api/v1/youtube/social-accounts', data);
        toast('Cuenta añadida', 'success');
        renderYouTube(root);
      } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
    } }),
  ]));

  if (r.accounts.length === 0) {
    content.appendChild(emptyState('📱', 'Sin cuentas configuradas', 'Añade tus cuentas TikTok / Instagram / YouTube Shorts para empezar a publicar multi-cuenta.'));
    return;
  }

  const byPlatform = {};
  r.accounts.forEach((a) => { if (!byPlatform[a.platform]) byPlatform[a.platform] = []; byPlatform[a.platform].push(a); });
  Object.entries(byPlatform).forEach(([platform, accounts]) => {
    content.appendChild(h('div', { class: 'mb-4' }, [
      h('h4', { class: 'font-semibold mb-2' }, `${platform === 'tiktok' ? '🎵' : platform === 'instagram' ? '📷' : platform === 'youtube-shorts' ? '🎬' : '𝕏'} ${platform} (${accounts.length})`),
      h('div', { class: 'grid grid-cols-1 md:grid-cols-3 gap-2' }, accounts.map((a) => h('div', { class: 'bg-base-800/50 border border-base-700 rounded p-3 text-sm' }, [
        h('div', { class: 'font-semibold' }, `@${a.accountHandle}`),
        h('div', { class: 'text-xs text-ink-400' }, `${a.niche || 'sin nicho'} · ${a.uploadStrategy}`),
        a.followers > 0 ? h('div', { class: 'text-xs text-ink-300' }, `${a.followers.toLocaleString()} followers`) : null,
        h('button', { class: 'text-xs text-red-400 hover:underline mt-2', onclick: async () => {
          if (!confirm(`¿Eliminar @${a.accountHandle}?`)) return;
          await apiClient.delete(`/api/v1/youtube/social-accounts/${a.id}`);
          renderYouTube(root);
        } }, 'Eliminar'),
      ]))),
    ]));
  });
}

// ============= CALENDARIO UNIFICADO =============
async function renderCalendario(content, root) {
  content.appendChild(skeletonCard(3));
  const r = await apiClient.get('/api/v1/youtube/calendar/unified').catch(() => ({ events: [], conflicts: [], sources: [] }));
  content.innerHTML = '';

  // Source toggles
  let activeSources = new Set(r.sources);
  const togglesRow = h('div', { class: 'flex gap-2 mb-4 flex-wrap' });
  const renderEvents = () => {
    const list = h('div', { class: 'space-y-1' });
    const filtered = r.events.filter((e) => activeSources.has(e.source));
    if (filtered.length === 0) {
      list.appendChild(emptyState('📅', 'Sin eventos en el rango seleccionado', ''));
    } else {
      filtered.forEach((e) => {
        const date = new Date(e.startsAt);
        list.appendChild(h('div', { class: 'flex items-center gap-3 bg-base-800/50 border border-base-700 rounded-lg p-3' }, [
          h('div', { class: 'text-xs text-ink-400 w-32' }, date.toLocaleString('es-ES', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })),
          h('span', { class: `px-2 py-0.5 rounded text-xs ${e.source === 'youtube' ? 'bg-red-500/15 text-red-300' : e.source === 'tareas' ? 'bg-amber-500/15 text-amber-300' : 'bg-emerald-500/15 text-emerald-300'}` }, e.source),
          h('div', { class: 'flex-1 text-sm' }, e.title),
          h('span', { class: 'text-xs text-ink-400' }, e.type),
        ]));
      });
    }
    const wrapResult = content.querySelector('#cal-events-wrap');
    if (wrapResult) {
      wrapResult.innerHTML = '';
      wrapResult.appendChild(list);
    }
  };

  r.sources.forEach((s) => {
    const btnEl = h('button', { class: 'px-3 py-1.5 rounded-lg text-sm bg-base-700 text-ink-100 border border-base-700 hover:border-gold-400/40' }, [
      h('span', {}, `${s === 'youtube' ? '🎬' : s === 'tareas' ? '✅' : '🏠'} ${s}`),
    ]);
    const updateBtn = () => {
      btnEl.className = `px-3 py-1.5 rounded-lg text-sm border transition ${activeSources.has(s) ? 'bg-gold-500/20 border-gold-400/40 text-gold-300' : 'bg-base-700/50 border-base-700 text-ink-400'}`;
    };
    updateBtn();
    btnEl.onclick = () => {
      if (activeSources.has(s)) activeSources.delete(s); else activeSources.add(s);
      updateBtn();
      renderEvents();
    };
    togglesRow.appendChild(btnEl);
  });
  togglesRow.appendChild(btn('🔄 Toggle todos', { class: 'ml-auto px-3 py-1.5 rounded-lg text-sm bg-base-700 hover:bg-base-600 text-ink-200', onclick: () => {
    if (activeSources.size === r.sources.length) { activeSources = new Set(); } else { activeSources = new Set(r.sources); }
    togglesRow.querySelectorAll('button').forEach((b, i) => {
      if (i < r.sources.length) {
        const s = r.sources[i];
        b.className = `px-3 py-1.5 rounded-lg text-sm border transition ${activeSources.has(s) ? 'bg-gold-500/20 border-gold-400/40 text-gold-300' : 'bg-base-700/50 border-base-700 text-ink-400'}`;
      }
    });
    renderEvents();
  } }));
  content.appendChild(togglesRow);

  // Conflictos detectados
  if (r.conflicts && r.conflicts.length > 0) {
    content.appendChild(h('div', { class: 'bg-amber-500/10 border border-amber-400/30 rounded-lg p-3 mb-4' }, [
      h('div', { class: 'font-semibold text-sm text-amber-300 mb-1' }, `⚠️ ${r.conflicts.length} conflictos detectados (eventos solapados)`),
      h('ul', { class: 'text-xs text-ink-300 space-y-0.5' },
        r.conflicts.slice(0, 5).map((c) => h('li', {}, `"${c.a.title}" (${c.a.source}) y "${c.b.title}" (${c.b.source}) — mismo horario`)),
      ),
    ]));
  }

  content.appendChild(btn('+ Nuevo evento YouTube', { class: 'mb-4 bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-3 py-1.5 rounded-lg text-sm', onclick: async () => {
    const data = await openModal({ title: 'Nuevo evento calendario YouTube', fields: [
      { name: 'title', label: 'Título', required: true },
      { name: 'eventType', label: 'Tipo', type: 'select', options: [
        { value: 'recording', label: '🎥 Grabación' },
        { value: 'editing', label: '✂️ Edición' },
        { value: 'publishing', label: '📤 Publicación' },
        { value: 'clip', label: '✂️ Clip' },
        { value: 'thumbnail', label: '🖼️ Thumbnail' },
        { value: 'live', label: '🔴 Live' },
      ], required: true },
      { name: 'startsAt', label: 'Inicio', type: 'datetime-local', required: true, help: 'Selecciona fecha y hora con el calendario' },
      { name: 'endsAt', label: 'Fin (opcional)', type: 'datetime-local' },
      { name: 'notes', label: 'Notas', type: 'textarea' },
    ] });
    if (!data) return;
    // datetime-local devuelve "YYYY-MM-DDTHH:mm" sin zona. Lo enviamos tal cual; el backend hace new Date() con eso (interpretado en zona local).
    if (data.endsAt === '' || data.endsAt == null) delete data.endsAt;
    try {
      await apiClient.post('/api/v1/youtube/calendar', data);
      toast('Evento creado', 'success');
      renderYouTube(root);
    } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
  } }));

  content.appendChild(h('div', { id: 'cal-events-wrap' }));
  renderEvents();
}

// ============= AGENTES YOUTUBE =============
async function renderAgentes(content) {
  const r = await apiClient.get('/api/v1/ai/agents').catch(() => ({ agents: [] }));
  const ytAgents = (r.agents || []).filter((a) => a.category === 'youtube' || a.id.startsWith('yt-'));
  content.innerHTML = '';
  content.appendChild(h('div', { class: 'mb-4' }, [
    h('h3', { class: 'font-semibold mb-1' }, `🤖 Agentes IA YouTube (${ytAgents.length})`),
    h('p', { class: 'text-xs text-ink-300' }, 'Estos agentes especializados están listos para usar desde el chat principal.'),
  ]));
  content.appendChild(h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' },
    ytAgents.map((a) => h('div', { class: 'bg-base-800/50 border border-base-700 rounded-lg p-3' }, [
      h('div', { class: 'flex items-center gap-2 mb-1' }, [
        h('span', { class: 'text-xl' }, a.emoji),
        h('span', { class: 'font-semibold text-sm' }, a.name),
      ]),
      h('p', { class: 'text-xs text-ink-300' }, a.description),
      h('a', { href: '#chat', class: 'text-xs text-gold-300 hover:underline mt-2 inline-block' }, 'Usar en Chat →'),
    ])),
  ));
}

function kpi(label, value, valueClass, subtitle) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl px-4 py-3' }, [
    h('div', { class: 'text-xs text-ink-400' }, label),
    h('div', { class: `text-xl font-bold ${valueClass}` }, value),
    subtitle ? h('div', { class: 'text-xs text-ink-500' }, subtitle) : null,
  ]);
}

// ============= STUDIO PRO (generador títulos/hooks/miniaturas/hashtags) =============
async function renderStudioPro(content, root) {
  content.appendChild(skeletonCard(1));
  let dashboard, niches;
  try {
    [dashboard, niches] = await Promise.all([
      apiClient.get('/api/v1/youtube/studio/dashboard'),
      apiClient.get('/api/v1/youtube/studio/niches'),
    ]);
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('⚠️', 'Error cargando Studio', e?.message || 'Reintenta', null));
    return;
  }
  content.innerHTML = '';

  // ===== HERO STATS =====
  const heroStats = h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-5' }, [
    spStat('🎯 Cuentas clipping', dashboard.accounts?.totalActive || 0, 'activas'),
    spStat('✂️ Clips totales', dashboard.clipsStats?.total || 0, `${dashboard.clipsStats?.queued || 0} en cola`),
    spStat('📱 Cuentas social', dashboard.socialAccountsActive || 0, 'conectadas'),
    spStat('🔥 Hooks banco', dashboard.totalHooksAvailable || 0, `${(dashboard.nichesAvailable || []).length} nichos`),
  ]);
  content.appendChild(heroStats);

  // ===== PRÓXIMOS SLOTS DE PUBLICACIÓN =====
  if (dashboard.nextSlots?.length) {
    content.appendChild(h('div', { class: 'bg-gradient-to-r from-emerald-500/10 via-base-900 to-gold-500/10 border border-emerald-400/30 rounded-2xl p-5 mb-5' }, [
      h('div', { class: 'flex items-center gap-2 mb-3' }, [
        h('span', { class: 'text-2xl' }, '⏰'),
        h('h3', { class: 'font-display font-bold text-lg text-emerald-300' }, 'Próximos slots óptimos · hoy'),
      ]),
      h('div', { class: 'grid grid-cols-2 md:grid-cols-3 gap-2' }, dashboard.nextSlots.map((s) => h('div', { class: 'bg-base-800/50 rounded-lg p-3 flex items-center justify-between' }, [
        h('div', {}, [
          h('div', { class: 'text-xs text-ink-400 uppercase tracking-wider' }, s.platform.replace('_', ' ')),
          h('div', { class: 'font-mono text-lg font-bold text-gold-300' }, s.time),
        ]),
        h('div', { class: 'text-xs text-ink-400' }, s.minutesAway > 0 ? `${s.minutesAway} min` : ''),
      ]))),
    ]));
  }

  // ===== GENERADOR EN VIVO =====
  const genBox = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-2xl p-5 mb-5' }, [
    h('div', { class: 'flex items-center gap-2 mb-4' }, [
      h('span', { class: 'text-2xl' }, '⚡'),
      h('h3', { class: 'font-display font-bold text-lg' }, 'Generador de contenido viral'),
    ]),
    h('div', { class: 'grid grid-cols-1 md:grid-cols-3 gap-3 mb-4' }, [
      h('div', {}, [
        h('label', { class: 'text-xs text-ink-300 block mb-1' }, '🎯 Nicho'),
        (() => {
          const sel = h('select', { id: 'sp-niche', class: 'w-full bg-base-900 border border-base-700 rounded-lg px-3 py-2 text-sm' });
          (niches.niches || []).forEach((n) => {
            const opt = document.createElement('option'); opt.value = n; opt.textContent = n;
            sel.appendChild(opt);
          });
          return sel;
        })(),
      ]),
      h('div', {}, [
        h('label', { class: 'text-xs text-ink-300 block mb-1' }, '🌐 Idioma'),
        (() => {
          const sel = h('select', { id: 'sp-lang', class: 'w-full bg-base-900 border border-base-700 rounded-lg px-3 py-2 text-sm' });
          ['es', 'en', 'fr', 'de'].forEach((l) => {
            const opt = document.createElement('option'); opt.value = l; opt.textContent = l.toUpperCase();
            sel.appendChild(opt);
          });
          return sel;
        })(),
      ]),
      h('div', {}, [
        h('label', { class: 'text-xs text-ink-300 block mb-1' }, '🎬 Variantes'),
        (() => {
          const sel = h('select', { id: 'sp-count', class: 'w-full bg-base-900 border border-base-700 rounded-lg px-3 py-2 text-sm' });
          [1, 3, 5, 10].forEach((n) => {
            const opt = document.createElement('option'); opt.value = String(n); opt.textContent = `${n} variante${n > 1 ? 's' : ''}`;
            sel.appendChild(opt);
          });
          sel.value = '3';
          return sel;
        })(),
      ]),
    ]),
    btn('⚡ Generar contenido viral', async () => {
      const niche = document.getElementById('sp-niche').value;
      const lang = document.getElementById('sp-lang').value;
      const count = Number(document.getElementById('sp-count').value);
      const out = document.getElementById('sp-output');
      out.innerHTML = '<div class="text-ink-300 text-sm py-4 text-center">⏳ Generando contenido...</div>';
      try {
        const r = await apiClient.post('/api/v1/youtube/studio/generate', { niche, language: lang, count });
        out.innerHTML = '';
        (r.variants || []).forEach((v, i) => out.appendChild(renderVariant(v, i + 1)));
      } catch (e) {
        out.innerHTML = `<div class="text-red-300 text-sm py-4 text-center">❌ ${e?.message || 'Error'}</div>`;
      }
    }, { variant: 'primary' }),
  ]);
  content.appendChild(genBox);

  const outputBox = h('div', { id: 'sp-output', class: 'space-y-4' });
  content.appendChild(outputBox);

  // ===== CALENDARIO SEMANAL ÓPTIMO =====
  if (dashboard.dailyPlan?.perPlatform) {
    const calBox = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-2xl p-5 mt-5' }, [
      h('h3', { class: 'font-display font-bold text-lg mb-4 flex items-center gap-2' }, ['📅', 'Plan diario sugerido']),
      h('div', { class: 'grid grid-cols-1 md:grid-cols-3 gap-3' }, Object.entries(dashboard.dailyPlan.perPlatform).map(([platform, data]) =>
        h('div', { class: 'bg-base-900/50 rounded-xl p-4' }, [
          h('div', { class: 'font-semibold text-gold-300 mb-2' }, platform.replace('_', ' ').toUpperCase()),
          h('div', { class: 'space-y-1' }, (data.slots || []).map((s, i) => h('div', { class: 'flex items-center justify-between text-sm' }, [
            h('span', { class: 'text-ink-200' }, `🕐 ${s}`),
            h('span', { class: 'text-xs text-ink-400 truncate' }, (data.recommended?.[i]?.title || '').slice(0, 40)),
          ]))),
        ])
      )),
    ]);
    content.appendChild(calBox);
  }
}

function spStat(label, value, sub) {
  return h('div', { class: 'bg-base-800/40 border border-base-700 rounded-xl p-3 text-center' }, [
    h('div', { class: 'text-xs text-ink-300' }, label),
    h('div', { class: 'text-2xl font-display font-bold text-gold-300' }, String(value)),
    h('div', { class: 'text-[10px] text-ink-500 mt-0.5' }, sub),
  ]);
}

function renderVariant(v, idx) {
  const thumbStyle = `background:${v.thumbnailConcept.bgColor};color:${v.thumbnailConcept.accentColor}`;
  const scoreColor = v.viralityScore >= 80 ? 'text-emerald-300' : v.viralityScore >= 60 ? 'text-gold-300' : 'text-ink-300';
  return h('div', { class: 'bg-base-800/60 border border-base-700 rounded-xl p-5 hover:border-gold-400/40 transition' }, [
    h('div', { class: 'flex items-start justify-between gap-3 mb-3' }, [
      h('div', { class: 'flex items-center gap-2' }, [
        h('span', { class: 'bg-gold-500/20 text-gold-300 px-2 py-0.5 rounded text-xs font-bold' }, `#${idx}`),
        h('span', { class: `text-2xl font-bold ${scoreColor}` }, `${v.viralityScore}/100`),
        h('span', { class: 'text-xs text-ink-400' }, '🔥 Viralidad'),
      ]),
      h('button', { class: 'text-xs text-ink-300 hover:text-gold-300', onclick: () => {
        const txt = `TÍTULO: ${v.title}\n\nDESCRIPCIÓN:\n${v.description}\n\nHASHTAGS: ${v.hashtags.join(' ')}\n\nCONCEPT MINIATURA: ${v.thumbnailConcept.style} · texto "${v.thumbnailConcept.overlayText}" + emoji ${v.thumbnailConcept.emoji}`;
        navigator.clipboard.writeText(txt);
        toast('Copiado todo al portapapeles', 'success');
      } }, '📋 Copiar todo'),
    ]),
    h('div', { class: 'grid grid-cols-1 md:grid-cols-3 gap-4' }, [
      // Miniatura concept
      h('div', { class: 'rounded-xl aspect-video flex flex-col items-center justify-center p-3 text-center', style: thumbStyle }, [
        h('div', { class: 'text-4xl mb-2' }, v.thumbnailConcept.emoji),
        h('div', { class: 'font-display font-black text-lg leading-tight' }, v.thumbnailConcept.overlayText),
        h('div', { class: 'text-[10px] mt-2 opacity-70' }, v.thumbnailConcept.style),
      ]),
      // Título + hook + horarios
      h('div', { class: 'md:col-span-2 space-y-2' }, [
        h('div', {}, [
          h('div', { class: 'text-[10px] text-ink-400 uppercase tracking-wider' }, '🎯 Título (' + v.titleScore + '/100)'),
          h('div', { class: 'font-semibold text-sm bg-base-900/50 px-3 py-2 rounded mt-1' }, v.title),
        ]),
        h('div', {}, [
          h('div', { class: 'text-[10px] text-ink-400 uppercase tracking-wider' }, '🪝 Hook'),
          h('div', { class: 'text-sm text-ink-200 italic' }, '"' + v.hook + '"'),
        ]),
        h('div', { class: 'flex flex-wrap gap-1' }, v.hashtags.map((tag) => h('span', { class: 'text-xs bg-gold-500/20 text-gold-300 px-2 py-0.5 rounded' }, tag))),
        h('div', { class: 'text-xs text-ink-400' }, '⏰ Mejor publicar: ' + (v.bestPostingTimes || []).map((b) => `${b.platform} ${b.nextSlot}`).join(' · ')),
      ]),
    ]),
    h('details', { class: 'mt-3 text-sm' }, [
      h('summary', { class: 'cursor-pointer text-ink-300 hover:text-gold-300' }, '📄 Ver descripción completa'),
      h('pre', { class: 'mt-2 bg-base-900/50 p-3 rounded text-xs whitespace-pre-wrap font-mono' }, v.description),
    ]),
  ]);
}

// ============================================================
// 🎬 CLIP-IA · Opus Clip clone (vídeo largo → 5 clips virales)
// ============================================================
async function renderClipIA(content, root) {
  content.innerHTML = '';

  // Header + explicación
  content.appendChild(h('div', { class: 'mb-4 p-4 bg-gradient-to-r from-gold-900/30 to-base-800 rounded-xl border border-gold-700/40' }, [
    h('h3', { class: 'font-display font-bold text-lg mb-1 flex items-center gap-2' }, [h('span', {}, '🎬'), h('span', {}, 'Procesador IA · Vídeo → Clips virales')]),
    h('p', { class: 'text-xs text-ink-300 leading-relaxed' }, 'Sube un vídeo largo (max 5GB). La IA transcribe con Whisper, detecta los 5 mejores momentos con Claude, los corta vertical 9:16, añade subtítulos quemados y los deja listos para descargar. Coste ~$0.20/vídeo de 30 min.'),
  ]));

  // Dropzone / botón upload
  const uploadCard = h('div', { class: 'mb-5 p-5 border-2 border-dashed border-base-600 hover:border-gold-400 rounded-xl bg-base-900/40 transition' });
  const fileInput = h('input', { type: 'file', accept: 'video/mp4,video/quicktime,video/x-msvideo,video/webm', style: 'display:none' });
  const titleInput = h('input', { type: 'text', class: 'w-full bg-base-800 border border-base-700 focus:border-gold-400 outline-none rounded-lg px-3 py-2 text-sm mb-2', placeholder: 'Título del vídeo (opcional)' });
  const nicheSelect = h('select', { class: 'w-full bg-base-800 border border-base-700 focus:border-gold-400 outline-none rounded-lg px-3 py-2 text-sm mb-2' }, [
    { value: '', label: '— Selecciona nicho —' },
    { value: 'autosell', label: '🚗 Autosell' },
    { value: 'inmobiliaria', label: '🏠 Inmobiliaria' },
    { value: 'trading', label: '📈 Trading' },
    { value: 'emprender', label: '💼 Emprender' },
    { value: 'lifestyle', label: '✨ Lifestyle' },
    { value: 'ai_agency', label: '🤖 AI Agency' },
  ].map((o) => { const op = document.createElement('option'); op.value = o.value; op.textContent = o.label; if (o.value === 'opus-yellow-pop') op.selected = true; return op; }));
  // === PANEL SUBTÍTULOS estilo Opus ===
  const tplState = { value: 'opus-yellow-pop' };
  const posState = { value: 'bottom' };
  const tplSelect = h('input', { type: 'hidden', name: 'subtitleTemplate', value: tplState.value });
  const posInput = h('input', { type: 'hidden', name: 'subtitlePosition', value: posState.value });

  // Header colapsable
  const subPanel = h('div', { class: 'mb-3 bg-base-900/50 border border-base-700 rounded-lg p-3' });
  const subHeader = h('div', { class: 'flex items-center justify-between cursor-pointer mb-2' });
  const subTitle = h('div', { class: 'text-sm font-semibold text-gold-300' }, '✨ Subtítulos PRO · click para personalizar');
  const subArrow = h('span', { class: 'text-ink-400 text-xs' }, '▼');
  subHeader.appendChild(subTitle);
  subHeader.appendChild(subArrow);
  subPanel.appendChild(subHeader);

  const subBody = h('div', { class: 'mt-3 hidden' });

  // Selector POSICIÓN visual
  const posLabel = h('div', { class: 'text-xs font-semibold text-ink-300 uppercase tracking-wide mb-2' }, 'Posición en pantalla');
  const posRow = h('div', { class: 'grid grid-cols-3 gap-2 mb-4' });
  const positions = [
    { value: 'top', label: 'Arriba', preview: 'top' },
    { value: 'middle', label: 'Centro', preview: 'mid' },
    { value: 'bottom', label: 'Abajo (default)', preview: 'bot' },
  ];
  const posBtns = [];
  positions.forEach((p) => {
    const btn = h('div', {
      class: 'cursor-pointer p-2 border-2 rounded-lg transition flex flex-col items-center gap-1 ' + (p.value === posState.value ? 'border-gold-400 bg-gold-500/10' : 'border-base-700 hover:border-base-600'),
      onclick: () => {
        posState.value = p.value;
        posInput.value = p.value;
        posBtns.forEach((b) => b.btn.className = 'cursor-pointer p-2 border-2 rounded-lg transition flex flex-col items-center gap-1 ' + (b.val === p.value ? 'border-gold-400 bg-gold-500/10' : 'border-base-700 hover:border-base-600'));
      },
    });
    // mini preview 9:16 con texto en la posición
    const mini = h('div', { class: 'w-12 h-16 bg-gradient-to-b from-base-700 to-base-900 rounded relative overflow-hidden' });
    const txt = h('div', { class: 'absolute left-1 right-1 text-[8px] text-center font-bold text-yellow-400 leading-none ' + (p.preview === 'top' ? 'top-1' : p.preview === 'mid' ? 'top-1/2 -translate-y-1/2' : 'bottom-1') }, 'ABC');
    mini.appendChild(txt);
    btn.appendChild(mini);
    btn.appendChild(h('div', { class: 'text-xs text-ink-200' }, p.label));
    posBtns.push({ btn, val: p.value });
    posRow.appendChild(btn);
  });
  subBody.appendChild(posLabel);
  subBody.appendChild(posRow);

  // Selector TEMPLATE con cards
  const tplLabel = h('div', { class: 'text-xs font-semibold text-ink-300 uppercase tracking-wide mb-2' }, 'Estilo de subtítulos · 12 plantillas');
  const tplGrid = h('div', { class: 'grid grid-cols-2 md:grid-cols-3 gap-2 max-h-80 overflow-y-auto p-1' });
  subBody.appendChild(tplLabel);
  subBody.appendChild(tplGrid);

  // Cargar templates desde API
  apiClient.get('/api/v1/youtube/subtitle-templates').then((data) => {
    const cards = [];
    (data.templates || []).forEach((t) => {
      const card = h('div', {
        class: 'cursor-pointer p-3 border-2 rounded-lg transition ' + (t.value === tplState.value ? 'border-gold-400 bg-gold-500/10' : 'border-base-700 hover:border-base-600 bg-base-800/40'),
        onclick: () => {
          tplState.value = t.value;
          tplSelect.value = t.value;
          cards.forEach((cc) => cc.card.className = 'cursor-pointer p-3 border-2 rounded-lg transition ' + (cc.val === t.value ? 'border-gold-400 bg-gold-500/10' : 'border-base-700 hover:border-base-600 bg-base-800/40'));
        },
      });
      // Mini-preview vertical 9:16 con texto al estilo template
      const preview = h('div', { class: 'w-full aspect-[9/16] rounded mb-2 relative overflow-hidden ' + t.previewBg });
      const txtSize = t.wordsPerGroup >= 3 ? 'text-base' : 'text-lg';
      const sampleTxt = t.wordsPerGroup === 1 ? 'BOOM' : t.wordsPerGroup === 2 ? 'OYE ESTO' : 'ESTO ES VIRAL';
      const txtBlock = h('div', {
        class: 'absolute left-1 right-1 text-center ' + txtSize + ' font-black tracking-tight leading-none whitespace-nowrap',
        style: 'color:' + t.previewColor + ';text-shadow:0 0 4px #000,0 0 8px #000,2px 2px 0 #000,-2px -2px 0 #000;' + (posState.value === 'top' ? 'top:8px' : posState.value === 'middle' ? 'top:50%;transform:translateY(-50%)' : 'bottom:8px'),
      }, sampleTxt);
      preview.appendChild(txtBlock);
      card.appendChild(preview);
      card.appendChild(h('div', { class: 'text-xs font-bold text-ink-100 leading-tight' }, t.label));
      card.appendChild(h('div', { class: 'text-[10px] text-ink-400 mt-0.5 leading-tight' }, t.description));
      if (t.category === 'premium') card.appendChild(h('div', { class: 'absolute top-1 right-1 px-1.5 py-0.5 bg-gold-400/90 text-base-900 text-[9px] font-bold rounded' }, 'PRO'));
      cards.push({ card, val: t.value });
      tplGrid.appendChild(card);
    });
  }).catch(() => {
    tplGrid.appendChild(h('p', { class: 'text-xs text-red-400' }, 'No se pudieron cargar plantillas'));
  });

  // Click header para colapsar/expandir
  subHeader.onclick = () => {
    if (subBody.classList.contains('hidden')) {
      subBody.classList.remove('hidden');
      subArrow.textContent = '▲';
    } else {
      subBody.classList.add('hidden');
      subArrow.textContent = '▼';
    }
  };
  subPanel.appendChild(subBody);
  const durSelect = h('select', { class: 'w-full bg-base-800 border border-base-700 focus:border-gold-400 outline-none rounded-lg px-3 py-2 text-sm mb-2' }, [
    { value: '15', label: '15s - Hooks rapidos' },
    { value: '20', label: '20s - Punchy' },
    { value: '30', label: '30s - Estandar (recomendado)' },
    { value: '45', label: '45s - Storytelling corto' },
    { value: '60', label: '60s - Maximo Shorts/Reels' },
    { value: '90', label: '90s - Maximo TikTok' },
  ].map((o) => { const op = document.createElement('option'); op.value = o.value; op.textContent = o.label; if (o.value === '30') op.selected = true; return op; }));
  const langSelect = h('select', { class: 'w-full bg-base-800 border border-base-700 focus:border-gold-400 outline-none rounded-lg px-3 py-2 text-sm mb-3' }, [
    { value: 'es', label: '🇪🇸 Español' }, { value: 'en', label: '🇬🇧 English' }, { value: 'fr', label: '🇫🇷 Français' }, { value: 'pt', label: '🇵🇹 Português' },
  ].map((o) => { const op = document.createElement('option'); op.value = o.value; op.textContent = o.label; if (o.value === 'opus-yellow-pop') op.selected = true; return op; }));

  const uploadBtn = h('button', {
    class: 'w-full bg-gradient-to-r from-gold-400 to-gold-500 hover:from-gold-300 hover:to-gold-400 text-base-900 font-semibold px-4 py-3 rounded-lg text-sm transition shadow-lg shadow-gold-500/20',
    onclick: () => fileInput.click(),
  }, '📤 Seleccionar vídeo y subir');

  const progressEl = h('div', { class: 'mt-3 hidden' });

  fileInput.addEventListener('change', async () => {
    const f = fileInput.files?.[0]; if (!f) return;
    const sizeMB = (f.size / (1024 * 1024)).toFixed(1);
    progressEl.classList.remove('hidden');
    progressEl.innerHTML = '';
    progressEl.appendChild(h('div', { class: 'text-xs text-ink-300' }, `Subiendo "${f.name}" (${sizeMB} MB)…`));
    const bar = h('div', { class: 'mt-2 h-2 bg-base-700 rounded overflow-hidden' });
    const fill = h('div', { class: 'h-full bg-gradient-to-r from-gold-400 to-gold-500 transition-all', style: 'width:0%' });
    bar.appendChild(fill);
    progressEl.appendChild(bar);

    const fd = new FormData();
    fd.append('file', f);
    if (titleInput.value.trim()) fd.append('title', titleInput.value.trim());
    if (nicheSelect.value) fd.append('niche', nicheSelect.value);
    fd.append('language', langSelect.value || 'es');
    fd.append('subtitleTemplate', tplSelect.value || 'classic-yellow');
    fd.append('clipDurationTarget', durSelect.value || '30');

    try {
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) fill.style.width = `${Math.round((e.loaded / e.total) * 100)}%`; };
      const result = await new Promise((resolve, reject) => {
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(new Error(`HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.open('POST', '/api/v1/youtube/source-videos');
        xhr.withCredentials = true;
        xhr.send(fd);
      });
      toast(`✅ Subido. ${result.message}`, 'success');
      titleInput.value = '';
      fileInput.value = '';
      progressEl.classList.add('hidden');
      renderClipIA(content, root);
    } catch (e) {
      toast('Error: ' + (e?.message || e), 'error');
      progressEl.innerHTML = '';
      progressEl.classList.add('hidden');
    }
  });

  uploadCard.appendChild(titleInput);
  uploadCard.appendChild(nicheSelect);
  uploadCard.appendChild(subPanel);
  uploadCard.appendChild(tplSelect);
  uploadCard.appendChild(posInput);
  uploadCard.appendChild(durSelect);
  uploadCard.appendChild(langSelect);
  uploadCard.appendChild(uploadBtn);
  uploadCard.appendChild(fileInput);
  uploadCard.appendChild(progressEl);
  content.appendChild(uploadCard);

  // Lista de vídeos procesados
  let r;
  try {
    r = await apiClient.get('/api/v1/youtube/source-videos');
  } catch (e) {
    content.appendChild(h('p', { class: 'text-red-400 text-sm' }, 'Error cargando vídeos: ' + (e?.message || e)));
    return;
  }
  const videos = r.videos || [];

  if (videos.length === 0) {
    content.appendChild(emptyState('🎬', 'Sin vídeos subidos aún', 'Sube tu primer vídeo largo arriba para empezar a generar clips virales con IA.'));
    return;
  }

  content.appendChild(h('h4', { class: 'font-semibold mt-6 mb-3' }, `Mis vídeos (${videos.length})`));
  const list = h('div', { class: 'space-y-3' });
  videos.forEach((v) => list.appendChild(videoSourceCard(v, content, root)));
  content.appendChild(list);

  // Auto-refresh si hay alguno en proceso
  if (videos.some((v) => ['pending', 'transcribing', 'scoring', 'cutting'].includes(v.pipelineStatus))) {
    setTimeout(() => { if (_ytTab === 'clipia') renderClipIA(content, root); }, 8000);
  }
}

function videoSourceCard(v, content, root) {
  const statusBadge = {
    pending:      { color: 'bg-ink-500/20 text-ink-300', label: '⏳ En cola' },
    transcribing: { color: 'bg-blue-500/20 text-blue-300', label: '🎤 Transcribiendo audio' },
    scoring:      { color: 'bg-purple-500/20 text-purple-300', label: '🧠 IA puntuando momentos' },
    cutting:      { color: 'bg-amber-500/20 text-amber-300', label: '✂️ Cortando clips' },
    done:         { color: 'bg-emerald-500/20 text-emerald-300', label: '✅ Listo' },
    failed:       { color: 'bg-red-500/20 text-red-300', label: '❌ Falló' },
  }[v.pipelineStatus] || { color: 'bg-ink-500/20 text-ink-300', label: v.pipelineStatus };

  const card = h('div', { class: 'p-4 bg-base-800/60 border border-base-700 rounded-lg' }, [
    h('div', { class: 'flex items-start justify-between gap-3 mb-2' }, [
      h('div', { class: 'flex-1' }, [
        h('div', { class: 'font-semibold text-ink-100' }, v.title || v.filename),
        h('div', { class: 'text-xs text-ink-400 mt-0.5' }, `${v.sizeMB} MB · ${v.durationSec ? Math.floor(v.durationSec / 60) + 'm ' + (v.durationSec % 60) + 's' : 'duración pendiente'}${v.niche ? ' · ' + v.niche : ''}`),
      ]),
      h('span', { class: `px-2 py-1 rounded text-xs font-medium ${statusBadge.color}` }, statusBadge.label),
    ]),
    v.pipelineError ? h('div', { class: 'text-xs text-red-300 mt-1' }, '⚠️ ' + v.pipelineError) : null,
    v.pipelineStatus === 'done' ? h('div', { class: 'mt-2 text-xs text-ink-400' }, `${v.clipsGenerated} clips generados · coste $${(v.costUsd || 0).toFixed(3)}`) : null,
    h('div', { class: 'flex gap-2 mt-3' }, [
      v.pipelineStatus === 'done' ? h('button', {
        class: 'bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-3 py-1.5 rounded text-xs',
        onclick: () => openVideoSourceDetail(v.id),
      }, '👁️ Ver clips generados') : null,
      v.pipelineStatus === 'failed' ? h('button', {
        class: 'bg-blue-500/20 hover:bg-blue-500/30 border border-blue-400/40 text-blue-300 px-3 py-1.5 rounded text-xs',
        onclick: async () => {
          try { await apiClient.post(`/api/v1/youtube/source-videos/${v.id}/process`); toast('Reintentando…', 'success'); renderClipIA(content, root); }
          catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
        },
      }, '🔄 Reintentar') : null,
      h('button', {
        class: 'bg-red-500/20 hover:bg-red-500/30 border border-red-400/40 text-red-300 px-3 py-1.5 rounded text-xs ml-auto',
        onclick: async () => {
          if (!confirm('¿Borrar este vídeo y todos sus clips?')) return;
          try { await apiClient.delete(`/api/v1/youtube/source-videos/${v.id}`); toast('Borrado', 'success'); renderClipIA(content, root); }
          catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
        },
      }, '🗑️'),
    ]),
  ]);
  return card;
}

async function openVideoSourceDetail(id) {
  let r;
  try { r = await apiClient.get(`/api/v1/youtube/source-videos/${id}`); }
  catch (e) { toast('Error: ' + (e?.message || e), 'error'); return; }

  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto' });
  modal.appendChild(h('div', { class: 'p-5 border-b border-base-700 flex items-center justify-between sticky top-0 bg-base-900 z-10' }, [
    h('div', {}, [
      h('h3', { class: 'font-display font-bold text-lg' }, '🎬 ' + (r.source.title || r.source.filename)),
      h('p', { class: 'text-xs text-ink-400 mt-0.5' }, `${r.clips.length} clips generados · Total coste: $${(r.source.costUsd || 0).toFixed(3)}`),
    ]),
    h('button', { class: 'text-ink-400 hover:text-ink-100 text-2xl', onclick: () => overlay.remove() }, '×'),
  ]));

  if (r.clips.length === 0) {
    modal.appendChild(h('div', { class: 'p-8 text-center text-ink-400' }, 'Sin clips. Probablemente el proceso aún corre.'));
  } else {
    const grid = h('div', { class: 'p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' });
    r.clips.forEach((c) => {
      const card = h('div', { class: 'bg-base-800 border border-base-700 rounded-lg overflow-hidden' });
      // Vídeo preview (lazy)
      const vid = h('video', { src: c.videoUrl, controls: true, preload: 'metadata', style: 'width:100%;aspect-ratio:9/16;background:#000;object-fit:cover' });
      card.appendChild(vid);
      card.appendChild(h('div', { class: 'p-3 space-y-2' }, [
        h('div', { class: 'flex items-center gap-2' }, [
          h('span', { class: 'px-2 py-0.5 bg-gold-500/20 text-gold-300 rounded text-xs font-bold' }, `${Math.round((c.aiScore || 0) * 100)}%`),
          h('span', { class: 'text-xs text-ink-400' }, `${c.duration}s · ${c.startSec}-${c.endSec}s`),
        ]),
        h('h4', { class: 'font-semibold text-sm text-ink-100 leading-tight' }, c.clipTitle || 'Sin título'),
        c.caption ? h('p', { class: 'text-xs text-ink-300 line-clamp-3' }, c.caption) : null,
        c.aiReason ? h('details', { class: 'text-xs' }, [
          h('summary', { class: 'cursor-pointer text-ink-400 hover:text-gold-300' }, '💡 Por qué este momento'),
          h('p', { class: 'mt-1 text-ink-300' }, c.aiReason),
        ]) : null,
        h('div', { class: 'flex gap-1 flex-wrap' }, (Array.isArray(c.hashtagSets) && c.hashtagSets[0] ? c.hashtagSets[0] : []).slice(0, 5).map((tag) => h('span', { class: 'text-xs text-blue-300' }, tag))),
        h('button', { class: 'block w-full bg-red-500/20 hover:bg-red-500/30 border border-red-400/40 text-red-300 px-3 py-1.5 rounded text-xs mb-1', onclick: () => openDistributeModal(c.id) }, '📡 Distribuir a YT cuentas'), h('a', { href: c.videoUrl, download: `clip-${c.id}.mp4`, class: 'block text-center bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-3 py-1.5 rounded text-xs mt-2' }, '⬇️ Descargar MP4'),
      ]));
      grid.appendChild(card);
    });
    modal.appendChild(grid);
  }
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

// ============================================================
// 📡 CUENTAS YT MULTI-CUENTA + PUBLISH QUEUE
// ============================================================
async function renderCuentasYT(content, root) {
  content.innerHTML = '';
  let r;
  try { r = await apiClient.get('/api/v1/youtube/oauth/accounts'); }
  catch (e) { content.appendChild(h('p', { class: 'text-red-400 text-sm' }, 'Error: ' + (e?.message || e))); return; }
  const accounts = r.accounts || [];

  content.appendChild(h('div', { class: 'mb-4 p-4 bg-gradient-to-r from-red-900/30 to-base-800 rounded-xl border border-red-700/40' }, [
    h('h3', { class: 'font-display font-bold text-lg mb-1 flex items-center gap-2' }, [h('span', {}, '📡'), h('span', {}, 'Canales YouTube conectados')]),
    h('p', { class: 'text-xs text-ink-300' }, 'Conecta hasta 5-7 canales con OAuth oficial. CARLOS OS sube cada clip con variantes distintas (caption + hashtags) por canal para evitar shadowban de duplicados.'),
  ]));

  content.appendChild(h('button', {
    class: 'mb-4 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-semibold px-4 py-2.5 rounded-lg text-sm transition shadow-lg shadow-red-500/20',
    onclick: async () => {
      try {
        const { authUrl } = await apiClient.get('/api/v1/youtube/oauth/init');
        window.open(authUrl, '_blank', 'width=600,height=700');
        toast('🔓 Autoriza tu canal en la ventana nueva. Vuelve cuando termines.', 'info', 5000);
      } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
    },
  }, '➕ Conectar canal YouTube'));

  if (accounts.length === 0) {
    content.appendChild(emptyState('📡', 'Sin canales conectados', 'Conecta tu primer canal YouTube arriba para empezar a publicar automáticamente.'));
  } else {
    const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-3' });
    accounts.forEach((a) => grid.appendChild(accountCard(a, content, root)));
    content.appendChild(grid);
  }

  // Publish queue debajo
  content.appendChild(h('h4', { class: 'font-semibold mt-8 mb-3' }, '📅 Cola de publicación'));
  await renderPublishQueue(content);
}

function accountCard(a, content, root) {
  return h('div', { class: 'p-4 bg-base-800/60 border border-base-700 rounded-lg' }, [
    h('div', { class: 'flex items-center gap-3 mb-3' }, [
      a.channel_thumbnail ? h('img', { src: a.channel_thumbnail, class: 'w-12 h-12 rounded-full' }) : h('div', { class: 'w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center text-2xl' }, '📺'),
      h('div', { class: 'flex-1 min-w-0' }, [
        h('div', { class: 'font-semibold truncate' }, a.channel_title),
        h('div', { class: 'text-xs text-ink-400' }, `${a.subscribers || 0} subs · ${a.uploads_total || 0} uploads${a.uploads_failed ? ' · ' + a.uploads_failed + ' fails' : ''}`),
      ]),
      h('span', { class: `px-2 py-1 rounded text-xs ${a.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-ink-500/20 text-ink-300'}` }, a.enabled ? '✅ Activa' : '⏸️ Pausada'),
    ]),
    a.niche ? h('div', { class: 'text-xs text-ink-300 mb-2' }, `Nicho: ${a.niche}`) : null,
    h('div', { class: 'flex gap-2' }, [
      h('button', {
        class: 'flex-1 bg-base-700 hover:bg-base-600 text-ink-100 px-3 py-1.5 rounded text-xs',
        onclick: async () => {
          try { await apiClient.patch(`/api/v1/youtube/oauth/accounts/${a.id}`, { enabled: !a.enabled }); renderCuentasYT(content, root); }
          catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
        },
      }, a.enabled ? '⏸️ Pausar' : '▶️ Activar'),
      h('button', {
        class: 'flex-1 bg-gold-500/20 hover:bg-gold-500/30 border border-gold-400/40 text-gold-300 px-3 py-1.5 rounded text-xs',
        onclick: async () => {
          const niche = prompt('Nicho de esta cuenta (autosell / inmobiliaria / trading / ...)', a.niche || '');
          if (niche === null) return;
          try { await apiClient.patch(`/api/v1/youtube/oauth/accounts/${a.id}`, { niche }); renderCuentasYT(content, root); }
          catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
        },
      }, '✏️ Editar nicho'),
      h('button', {
        class: 'bg-red-500/20 hover:bg-red-500/30 border border-red-400/40 text-red-300 px-3 py-1.5 rounded text-xs',
        onclick: async () => {
          if (!confirm('¿Desconectar este canal? Tendrás que volver a hacer OAuth.')) return;
          try { await apiClient.delete(`/api/v1/youtube/oauth/accounts/${a.id}`); renderCuentasYT(content, root); }
          catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
        },
      }, '🗑️'),
    ]),
  ]);
}

async function renderPublishQueue(content) {
  let r;
  try { r = await apiClient.get('/api/v1/youtube/publish-queue'); }
  catch (e) { return; }
  const items = r.queue || [];
  if (items.length === 0) {
    content.appendChild(h('p', { class: 'text-xs text-ink-400' }, 'Sin publicaciones programadas. Distribuye un clip desde Clip IA para verlos aquí.'));
    return;
  }
  const statusEmoji = { pending: '⏳', uploading: '⬆️', published: '✅', failed: '❌', skipped: '⏭️' };
  const list = h('div', { class: 'space-y-1.5' });
  items.forEach((q) => {
    const date = new Date(q.scheduled_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
    list.appendChild(h('div', { class: 'flex items-center gap-3 p-2 bg-base-800/40 border border-base-700 rounded text-xs' }, [
      h('span', { class: 'text-base' }, statusEmoji[q.status] || '❓'),
      h('span', { class: 'text-ink-400 w-32 truncate' }, date),
      h('span', { class: 'flex-1 truncate' }, [q.variant_title || q.clip_title || '(sin título)', ' → ', h('strong', {}, q.account_title || '?')]),
      q.published_url ? h('a', { href: q.published_url, target: '_blank', class: 'text-emerald-300 hover:underline' }, '↗️ ver') : null,
      q.status === 'failed' ? h('button', {
        class: 'text-blue-300 hover:underline',
        onclick: async () => { try { await apiClient.post(`/api/v1/youtube/publish-queue/${q.id}/retry`); renderCuentasYT(content); } catch (e) { toast('Error', 'error'); } },
      }, '↻ retry') : null,
      h('button', {
        class: 'text-red-400 hover:underline',
        onclick: async () => { if (!confirm('¿Cancelar?')) return; try { await apiClient.delete(`/api/v1/youtube/publish-queue/${q.id}`); renderCuentasYT(content); } catch (e) { toast('Error', 'error'); } },
      }, '✕'),
    ]));
  });
  content.appendChild(list);
}

// === Distribuidor: modal para enviar clip a múltiples cuentas ===
async function openDistributeModal(clipId, content) {
  let r;
  try { r = await apiClient.get('/api/v1/youtube/oauth/accounts'); }
  catch (e) { toast('Error cargando cuentas: ' + (e?.message || e), 'error'); return; }
  const accounts = (r.accounts || []).filter((a) => a.enabled);
  if (accounts.length === 0) { toast('Conecta al menos 1 canal en pestaña Cuentas YT', 'warn'); return; }

  const checks = {};
  const overlay = h('div', { class: 'fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4', onclick: (e) => { if (e.target === overlay) overlay.remove(); } });
  const modal = h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-md w-full p-5' }, [
    h('h3', { class: 'font-display font-bold text-lg mb-1' }, '📡 Distribuir clip a cuentas'),
    h('p', { class: 'text-xs text-ink-300 mb-4' }, 'IA generará un título + caption + hashtags DISTINTOS para cada cuenta (evita shadowban).'),
    h('div', { class: 'space-y-2 mb-4' }, accounts.map((a) => {
      const cb = h('input', { type: 'checkbox', class: 'rounded' });
      checks[a.id] = cb;
      return h('label', { class: 'flex items-center gap-2 p-2 bg-base-800 rounded cursor-pointer hover:bg-base-700' }, [
        cb,
        h('span', { class: 'flex-1 text-sm' }, `${a.channel_title} (${a.subscribers || 0} subs)`),
      ]);
    })),
    h('label', { class: 'block text-xs font-semibold text-ink-300 uppercase mb-1' }, 'Empezar a publicar'),
    (() => { const i = h('input', { type: 'datetime-local', id: '_distStart', class: 'w-full bg-base-800 border border-base-700 rounded px-2 py-1.5 text-sm mb-3' }); return i; })(),
    h('label', { class: 'block text-xs font-semibold text-ink-300 uppercase mb-1' }, 'Espacio entre publicaciones'),
    (() => { const s = h('select', { id: '_distStagger', class: 'w-full bg-base-800 border border-base-700 rounded px-2 py-1.5 text-sm mb-4' }, [30, 60, 90, 120, 240, 480].map((m) => { const o = document.createElement('option'); o.value = String(m); o.textContent = m < 60 ? `${m} min` : `${m / 60}h`; if (m === 90) o.selected = true; return o; })); return s; })(),
    h('div', { class: 'flex gap-2 justify-end' }, [
      h('button', { class: 'bg-base-700 hover:bg-base-600 px-4 py-2 rounded text-sm', onclick: () => overlay.remove() }, 'Cancelar'),
      h('button', {
        class: 'bg-gradient-to-r from-gold-400 to-gold-500 text-base-900 font-semibold px-4 py-2 rounded text-sm',
        onclick: async () => {
          const accountIds = Object.entries(checks).filter(([, cb]) => cb.checked).map(([id]) => id);
          if (accountIds.length === 0) { toast('Selecciona al menos 1 cuenta', 'warn'); return; }
          const startEl = document.getElementById('_distStart');
          const staggerEl = document.getElementById('_distStagger');
          try {
            const r = await apiClient.post(`/api/v1/youtube/clips/${clipId}/distribute`, {
              accountIds, staggerMinutes: parseInt(staggerEl.value, 10), startAt: startEl.value || undefined,
            });
            toast(`✅ ${r.queued} publicaciones programadas`, 'success');
            overlay.remove();
          } catch (e) { toast('Error: ' + (e?.message || e), 'error'); }
        },
      }, '🚀 Distribuir'),
    ]),
  ]);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
}

// ============================================================
// 📊 ANALYTICS CLIP-IA
// ============================================================
async function renderAnalyticsClipIA(content, root) {
  content.innerHTML = '';
  content.appendChild(h('div', { class: 'mb-4 p-4 bg-gradient-to-r from-blue-900/30 to-base-800 rounded-xl border border-blue-700/40' }, [
    h('h3', { class: 'font-display font-bold text-lg mb-1' }, '📊 Analytics CLIP-IA'),
    h('p', { class: 'text-xs text-ink-300' }, 'Métricas globales del pipeline: vídeos procesados, clips generados, performance por cuenta y por nicho. Stats se sincronizan cada 6h.'),
  ]));

  // Overview KPIs
  try {
    const r = await apiClient.get('/api/v1/youtube/analytics/overview');
    const o = r.overview || {};
    const kpi = (label, value, color) => h('div', { class: `p-3 bg-base-800/60 border border-base-700 rounded-lg text-center` }, [
      h('div', { class: `text-2xl font-bold ${color || 'text-gold-300'}` }, String(value ?? 0)),
      h('div', { class: 'text-xs text-ink-400 uppercase tracking-wide mt-1' }, label),
    ]);
    content.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-5 gap-2 mb-6' }, [
      kpi('Vídeos procesados', o.videos_processed, 'text-blue-300'),
      kpi('Clips generados', o.total_clips, 'text-purple-300'),
      kpi('Publicados', o.published, 'text-emerald-300'),
      kpi('Pendientes', o.pending, 'text-amber-300'),
      kpi('Canales activos', o.accounts_active, 'text-gold-300'),
      kpi('Total views', (o.total_views || 0).toLocaleString('es-ES'), 'text-cyan-300'),
      kpi('Total likes', (o.total_likes || 0).toLocaleString('es-ES'), 'text-pink-300'),
      kpi('Fallos', o.failed, 'text-red-300'),
      kpi('Coste total', '$' + (o.total_cost_usd || 0).toFixed(2), 'text-gold-300'),
    ]));
  } catch (e) { content.appendChild(h('p', { class: 'text-red-400 text-xs mb-4' }, 'Error overview: ' + (e?.message || e))); }

  // Top clips
  try {
    const r = await apiClient.get('/api/v1/youtube/analytics/top-clips');
    const items = r.clips || [];
    content.appendChild(h('h4', { class: 'font-semibold mt-4 mb-2' }, `🏆 Top clips por views (${items.length})`));
    if (items.length === 0) {
      content.appendChild(h('p', { class: 'text-xs text-ink-400 mb-4' }, 'Sin clips aún. Sube un vídeo desde Clip IA → distribuye a tus canales → verás aquí las métricas.'));
    } else {
      const list = h('div', { class: 'space-y-1.5 mb-6' });
      items.slice(0, 10).forEach((c, i) => {
        list.appendChild(h('div', { class: 'flex items-center gap-3 p-2.5 bg-base-800/60 border border-base-700 rounded text-sm' }, [
          h('span', { class: 'w-7 text-center font-bold text-gold-300' }, `#${i + 1}`),
          h('span', { class: 'flex-1 truncate' }, c.clip_title || '(sin título)'),
          h('span', { class: 'text-xs text-ink-400' }, `Score IA ${Math.round((c.ai_score || 0) * 100)}%`),
          h('span', { class: 'text-emerald-300 font-semibold' }, `${(c.total_views || 0).toLocaleString('es-ES')} views`),
          h('span', { class: 'text-pink-300 text-xs' }, `❤️ ${(c.total_likes || 0).toLocaleString('es-ES')}`),
          h('span', { class: 'text-xs text-ink-400' }, `${c.published_to_accounts || 0} cuentas`),
        ]));
      });
      content.appendChild(list);
    }
  } catch { /* skip */ }

  // Top accounts
  try {
    const r = await apiClient.get('/api/v1/youtube/analytics/top-accounts');
    const items = r.accounts || [];
    content.appendChild(h('h4', { class: 'font-semibold mb-2' }, `📡 Performance por canal (${items.length})`));
    if (items.length === 0) {
      content.appendChild(h('p', { class: 'text-xs text-ink-400 mb-4' }, 'Conecta canales en Cuentas YT.'));
    } else {
      const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 gap-2 mb-6' });
      items.forEach((a) => {
        grid.appendChild(h('div', { class: 'p-3 bg-base-800/60 border border-base-700 rounded flex items-center gap-3' }, [
          a.channel_thumbnail ? h('img', { src: a.channel_thumbnail, class: 'w-10 h-10 rounded-full' }) : h('div', { class: 'w-10 h-10 rounded-full bg-red-500/20' }),
          h('div', { class: 'flex-1 min-w-0' }, [
            h('div', { class: 'font-semibold text-sm truncate' }, a.channel_title),
            h('div', { class: 'text-xs text-ink-400' }, `${a.subscribers || 0} subs · ${a.niche || 'sin-nicho'}`),
          ]),
          h('div', { class: 'text-right text-xs' }, [
            h('div', { class: 'text-emerald-300 font-semibold' }, `${(a.total_views || 0).toLocaleString('es-ES')} views`),
            h('div', { class: 'text-ink-400' }, `${a.published || 0} publicados · avg ${Math.round(a.avg_views_per_clip || 0)}/clip`),
          ]),
        ]));
      });
      content.appendChild(grid);
    }
  } catch { /* skip */ }

  // By niche
  try {
    const r = await apiClient.get('/api/v1/youtube/analytics/by-niche');
    const items = r.byNiche || [];
    content.appendChild(h('h4', { class: 'font-semibold mb-2' }, '🎯 Performance por nicho'));
    if (items.length === 0) {
      content.appendChild(h('p', { class: 'text-xs text-ink-400' }, 'Sin datos.'));
    } else {
      const list = h('div', { class: 'space-y-1.5' });
      items.forEach((n) => {
        list.appendChild(h('div', { class: 'flex items-center gap-3 p-2 bg-base-800/40 border border-base-700 rounded text-xs' }, [
          h('span', { class: 'w-24 text-gold-300 font-semibold' }, n.niche || 'sin-nicho'),
          h('span', { class: 'flex-1' }, `${n.videos || 0} vídeos · ${n.clips || 0} clips · ${n.published || 0} publicados`),
          h('span', { class: 'text-emerald-300' }, `${(n.total_views || 0).toLocaleString('es-ES')} views`),
          h('span', { class: 'text-ink-400' }, `avg ${Math.round(n.avg_views || 0)}/clip`),
        ]));
      });
      content.appendChild(list);
    }
  } catch { /* skip */ }
}

// ============================================================
// 📊 ANALYTICS CLIP-IA
// ============================================================
